import React from 'react'

import styled from 'styled-components'

import Modal from 'components/Modal'
import ModalButtons from 'components/Modal/buttons'
import ModalTypography from 'components/Modal/typography'
import { TrackClicked, TrackViewed } from 'components/trackers'
import { Heading } from 'components/typography'
import { style } from 'theme'

const ModalMessage = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 50px;
  overflow: auto;
`
const AdditionalModalMessage = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 50px;
  overflow: auto;
  border-top: 1px solid ${style((theme) => theme.colors.ivory)};
  width: 100%;
`

export const ModalImage = styled.img<{ imageMax?: string }>`
  margin: 0 auto;
  position: absolute;
  z-index: 6;
  left: 25px;
  top: 50%;
  -ms-transform: translateY(-50%);
  transform: translateY(-50%);
  ${({ imageMax }) =>
    imageMax === 'none'
      ? 'max-width: none'
      : imageMax === 'large'
        ? 'max-width: 200px'
        : 'max-width: 104px'}

  @media screen and (max-height: ${style((theme) => theme.breakpoints.sm)}px) {
    width: calc(100% / 5.5);
    ${({ imageMax }) =>
      imageMax === 'none'
        ? 'max-width: none'
        : imageMax === 'large'
          ? 'max-width: 200px'
          : 'max-width: 104px'}
    margin: 0 auto 10px;
  }
`
const ImgGroup = styled.div`
  height: 100px;
  width: 100px;
  position: relative;
  margin: 0 auto;
  margin-bottom: 30px;
`
const ModalImageBackground = styled.div`
  margin: 0 auto;
  background: ${style((theme) => theme.colors.ivory)};
  box-shadow: ${style((theme) => theme.shadows.smMedium)};
  height: 80px;
  width: 80px;
  z-index: 3;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 10px;
  -ms-transform: translateY(-50%);
  transform: translateY(-50%);
`
const OuterModalImageBackground = styled.div`
  background: ${style((theme) => theme.colors.ivory)};
  height: 100px;
  width: 100px;
  z-index: 2;
  margin: 0 auto;
  border-radius: 50%;
`
const ModalHeader = styled(Heading)`
  margin-bottom: 8px;
  text-align: center;
`

const AdditionalTitle = styled(ModalTypography.Title)`
  margin-top: 50px;
`
const Anchor = styled.a`
  width: 100%;
  text-decoration: none;

  &:not(:only-child):not(:last-child) {
    border-right: 1px solid ${style((theme) => theme.colors.ivory)};
  }
`
const StyledBody = styled(ModalTypography.Body)`
  margin-top: 12px;
`
export enum ModalActionTypes {
  danger = 'danger',
  primary = 'primary',
  secondary = 'secondary',
}

export type Handler = () => void

export type ModalAction = {
  handler?: Handler
  href?: string
  eventFields?: object
  label: string
  type: ModalActionTypes
  testId?: string
}

export enum ButtonStyle {
  col,
  row,
}

export type Props = {
  actions: ModalAction[]
  additionalHeader?: string
  additionalTitle?: string
  additionalBody?: string | React.ReactNode | (() => React.ReactNode)
  buttonStyle?: ButtonStyle
  body?: string | React.ReactNode | (() => React.ReactNode)
  imageMax?: 'none' | 'large'
  eventFields?: object
  header?: string | React.ReactNode | (() => React.ReactNode)
  image?: string
  imageAlt?: string
  isOpen: boolean
  className?: string
  onRequestClose?: () => void
  title?: string | React.ReactNode | (() => React.ReactNode)
  closeOnOutsideClick?: boolean
  onCloseAnimationComplete?: () => void
}

const actionTypeMap = {
  danger: ModalButtons.Danger,
  primary: ModalButtons.Primary,
  secondary: ModalButtons.Secondary,
}

const Buttons = ({ buttonStyle = ButtonStyle.row, children }) => (
  <>
    {buttonStyle === ButtonStyle.row ? (
      <ModalButtons.Row>{children}</ModalButtons.Row>
    ) : (
      <ModalButtons.Col>{children}</ModalButtons.Col>
    )}
  </>
)

const renderContent = (
  prop: string | React.ReactNode | (() => React.ReactNode),
) => (typeof prop === 'function' ? prop() : prop)

// Necessary for modals that may open multiple times on a single page
const ConditionalTrackViewed = ({ track, fields, children }) =>
  track && fields ? (
    <TrackViewed fields={fields} multiple>
      {children}
    </TrackViewed>
  ) : (
    children
  )

// Prevent the wrapping tracker component from breaking the flex layout
const TrackedButton = ModalButtons.Button.withComponent(TrackClicked)

const ConditionalTrackClicked = ({ fields, children, label }) =>
  fields ? (
    <TrackedButton fields={{ ...fields, cta_title: label }}>
      {children}
    </TrackedButton>
  ) : (
    children
  )

export default (props: Props) => (
  <Modal
    className={props.className}
    closeOnEsc={true}
    isOpen={props.isOpen}
    onRequestClose={props.onRequestClose}
    data-testid={props['data-testid']}
    closeOnOutsideClick={
      props.closeOnOutsideClick ? props.closeOnOutsideClick : true
    }
    onCloseAnimationComplete={props.onCloseAnimationComplete}
  >
    {() => (
      <>
        <ConditionalTrackViewed track={props.isOpen} fields={props.eventFields}>
          <ModalMessage>
            {!props.additionalBody && props.image && (
              <ImgGroup>
                <OuterModalImageBackground>
                  <ModalImageBackground>
                    <ModalImage
                      alt={props.imageAlt ?? ''}
                      imageMax={props.imageMax}
                      src={props.image}
                    />
                  </ModalImageBackground>
                </OuterModalImageBackground>
              </ImgGroup>
            )}
            {props.title && (
              <ModalTypography.Title>
                {renderContent(props.title)}
              </ModalTypography.Title>
            )}
            {props.header && (
              <ModalHeader>{renderContent(props.header)}</ModalHeader>
            )}
            {props.body && <StyledBody>{renderContent(props.body)}</StyledBody>}
          </ModalMessage>
          {props.additionalBody && (
            <AdditionalModalMessage>
              {props.additionalTitle && (
                <AdditionalTitle>
                  {renderContent(props.additionalTitle)}
                </AdditionalTitle>
              )}
              {props.additionalHeader && (
                <ModalHeader>
                  {renderContent(props.additionalHeader)}
                </ModalHeader>
              )}
              {props.additionalBody && (
                <ModalTypography.Body>
                  {renderContent(props.additionalBody)}
                </ModalTypography.Body>
              )}
            </AdditionalModalMessage>
          )}
        </ConditionalTrackViewed>

        <Buttons buttonStyle={props.buttonStyle}>
          {props.actions.map((action, index) => {
            const Button = actionTypeMap[action.type]
            return (
              <ConditionalTrackClicked
                fields={action.eventFields}
                label={action.label}
                key={index}
              >
                {action.href ? (
                  <Anchor
                    href={action.href}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <Button data-testid={`button_${index}`}>
                      {action.label}
                    </Button>
                  </Anchor>
                ) : (
                  <Button
                    onClick={action.handler}
                    data-testid={`button_${index}`}
                  >
                    {action.label}
                  </Button>
                )}
              </ConditionalTrackClicked>
            )
          })}
        </Buttons>
      </>
    )}
  </Modal>
)
